import React, { useState, useEffect } from 'react';
import axios from 'axios';
import constant from '../constants/constant';
import { Coins, Heart, Save, RefreshCw, Layers, CheckCircle, Search, Sparkles, ArrowRight } from 'lucide-react';

const LevelSettings = () => {
  const [activeTab, setActiveTab] = useState('wealth'); // 'wealth' or 'charm'
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const [wealthLevels, setWealthLevels] = useState([]);
  const [charmLevels, setCharmLevels] = useState([]);

  const [toast, setToast] = useState({ message: '', type: '', visible: false });

  const showToast = (message, type = 'success') => {
    setToast({ message, type, visible: true });
    setTimeout(() => setToast({ message: '', type: '', visible: false }), 4000);
  };

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${constant.API_BASE_URL}/levels/config`);
      if (res.data && res.data.success && res.data.data) {
        const { wealth_levels, charm_levels } = res.data.data;
        
        // Ensure levels 1 to 100 exist in state
        setWealthLevels(ensure100Levels(wealth_levels || []));
        setCharmLevels(ensure100Levels(charm_levels || []));
      }
    } catch (err) {
      console.error("Error fetching level configs:", err);
      showToast("Failed to load level configs: " + (err.response?.data?.message || err.message), "error");
    } finally {
      setLoading(false);
    }
  };

  const ensure100Levels = (existingList) => {
    const map = new Map();
    existingList.forEach(item => map.set(Number(item.level), Number(item.exp_required)));

    const result = [];
    for (let l = 1; l <= 100; l++) {
      let exp = map.has(l) ? map.get(l) : 0;
      if (!map.has(l)) {
        if (l === 1) exp = 0;
        else if (l === 2) exp = 1000;
        else if (l === 3) exp = 5000;
        else if (l === 4) exp = 15000;
        else exp = Math.floor(35000 + Math.pow(l - 5, 2.1) * 20000);
      }
      result.push({ level: l, exp_required: exp });
    }
    return result;
  };

  const handleExpChange = (levelNum, value) => {
    const val = Math.max(0, parseInt(value) || 0);
    if (activeTab === 'wealth') {
      setWealthLevels(prev => prev.map(item => item.level === levelNum ? { ...item, exp_required: val } : item));
    } else {
      setCharmLevels(prev => prev.map(item => item.level === levelNum ? { ...item, exp_required: val } : item));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const currentList = activeTab === 'wealth' ? wealthLevels : charmLevels;
      
      const token = sessionStorage.getItem('adminToken');
      const res = await axios.post(`${constant.API_BASE_URL}/levels/admin/config`, {
        type: activeTab,
        levels: currentList
      }, {
        headers: { Authorization: token ? `Bearer ${token}` : '' }
      });

      if (res.data && res.data.success) {
        showToast(`Successfully saved ${activeTab.toUpperCase()} Level Configurations (1-100)!`);
      } else {
        showToast(res.data?.message || 'Error saving level config', 'error');
      }
    } catch (err) {
      console.error("Error saving level config:", err);
      showToast("Error saving levels: " + (err.response?.data?.message || err.message), 'error');
    } finally {
      setSaving(false);
    }
  };

  const currentLevels = activeTab === 'wealth' ? wealthLevels : charmLevels;
  const filteredLevels = currentLevels.filter(item => 
    item.level.toString().includes(searchQuery) ||
    item.exp_required.toString().includes(searchQuery)
  );

  return (
    <div className="space-y-6 animate-in fade-in duration-300 pb-12">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-extrabold text-slate-800 flex items-center gap-3">
            <Layers className="text-pink-600" size={32} /> Level Threshold Settings (1 to 100)
          </h2>
          <p className="text-slate-500 mt-1">
            Configure Superadmin thresholds for Wealth (Sending Coins) & Charm (Receiving Coins) levels.
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={fetchConfigs}
            disabled={loading}
            className="flex items-center gap-2 bg-white text-slate-700 px-5 py-2.5 rounded-xl font-semibold shadow-sm border border-slate-200 hover:bg-slate-50 transition"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 bg-pink-600 text-white px-6 py-2.5 rounded-xl font-bold shadow-lg shadow-pink-500/30 hover:bg-pink-700 transition"
          >
            <Save size={18} className={saving ? 'animate-pulse' : ''} /> 
            {saving ? 'Saving...' : `Save ${activeTab.toUpperCase()} Configs`}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex bg-slate-100 p-1.5 rounded-2xl max-w-md border border-slate-200 shadow-inner">
        <button
          onClick={() => setActiveTab('wealth')}
          className={`flex-1 flex items-center justify-center gap-2.5 py-3 px-4 rounded-xl font-extrabold text-sm transition-all ${
            activeTab === 'wealth'
              ? 'bg-gradient-to-r from-amber-500 to-yellow-600 text-white shadow-md'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          <Coins size={18} /> 🪙 Wealth Levels (Sending)
        </button>
        <button
          onClick={() => setActiveTab('charm')}
          className={`flex-1 flex items-center justify-center gap-2.5 py-3 px-4 rounded-xl font-extrabold text-sm transition-all ${
            activeTab === 'charm'
              ? 'bg-gradient-to-r from-pink-500 to-rose-600 text-white shadow-md'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          <Heart size={18} /> 💖 Charm Levels (Receiving)
        </button>
      </div>

      {/* Info Card */}
      <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white p-6 rounded-2xl shadow-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border border-indigo-900/50">
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-pink-400 font-bold text-xs uppercase tracking-wider">
            <Sparkles size={16} /> Real-Time Level Engine Sync
          </div>
          <h3 className="text-xl font-bold">
            Configuring {activeTab === 'wealth' ? '🪙 Wealth Level Thresholds' : '💖 Charm Level Thresholds'}
          </h3>
          <p className="text-slate-300 text-xs max-w-2xl leading-relaxed">
            Levels 1 to 100 require cumulative coin EXP thresholds. The Mobile App fetches these thresholds live via <code className="bg-white/10 px-1.5 py-0.5 rounded text-pink-300">/api/levels/config</code>.
          </p>
        </div>
        <div className="bg-white/10 backdrop-blur-md px-4 py-3 rounded-xl border border-white/10 text-right">
          <div className="text-xs font-semibold text-slate-300">Total Configured Levels</div>
          <div className="text-2xl font-black text-amber-300">{currentLevels.length} Levels</div>
        </div>
      </div>

      {/* Filter / Search Bar */}
      <div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 w-full max-w-md bg-slate-50 px-4 py-2.5 rounded-xl border border-slate-200">
          <Search className="text-slate-400" size={18} />
          <input
            type="text"
            placeholder="Search Level (e.g. 5, 25, 100) or EXP amount..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-transparent border-none outline-none text-slate-700 text-sm font-semibold placeholder-slate-400"
          />
        </div>
        <div className="text-xs font-bold text-slate-400">
          Showing {filteredLevels.length} of 100 Levels
        </div>
      </div>

      {/* Level Thresholds Table */}
      <div className="bg-white rounded-2xl shadow-xl shadow-slate-100/50 border border-slate-100 overflow-hidden">
        {loading ? (
          <div className="p-20 text-center text-slate-500 font-semibold flex items-center justify-center gap-3">
            <RefreshCw className="animate-spin text-pink-600" size={24} /> Loading Level Configurations...
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 text-slate-400 text-xs font-extrabold uppercase tracking-wider border-b border-slate-100">
                  <th className="p-5 w-32">Level #</th>
                  <th className="p-5">Required EXP / Coins (Cumulative)</th>
                  <th className="p-5">Formatted Display</th>
                  <th className="p-5 text-right">Preview Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {filteredLevels.map((item) => {
                  const numVal = Number(item.exp_required || 0);
                  let formatted = `${numVal.toLocaleString()} Coins`;
                  if (numVal >= 1000000000) formatted = `💎 ${(numVal / 1000000000).toFixed(1)}B Coins`;
                  else if (numVal >= 1000000) formatted = `💎 ${(numVal / 1000000).toFixed(1)}M Coins`;
                  else if (numVal >= 1000) formatted = `💎 ${(numVal / 1000).toFixed(0)}K Coins`;

                  return (
                    <tr key={item.level} className="hover:bg-slate-50/70 transition">
                      <td className="p-5">
                        <div className="flex items-center gap-2">
                          <span className={`w-8 h-8 rounded-xl flex items-center justify-center font-black text-xs ${
                            activeTab === 'wealth' 
                              ? 'bg-amber-100 text-amber-800 border border-amber-200' 
                              : 'bg-pink-100 text-pink-800 border border-pink-200'
                          }`}>
                            L{item.level}
                          </span>
                          <span className="font-bold text-slate-800">Level {item.level}</span>
                        </div>
                      </td>
                      <td className="p-5">
                        <div className="flex items-center gap-3">
                          <input
                            type="number"
                            min="0"
                            value={item.exp_required}
                            onChange={(e) => handleExpChange(item.level, e.target.value)}
                            className="w-full max-w-xs px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-800 focus:bg-white focus:border-pink-500 focus:ring-2 focus:ring-pink-200 outline-none transition"
                          />
                          <span className="text-xs font-semibold text-slate-400">Coins</span>
                        </div>
                      </td>
                      <td className="p-5">
                        <span className="font-extrabold text-slate-700 bg-slate-100 px-3 py-1 rounded-lg text-xs">
                          {formatted}
                        </span>
                      </td>
                      <td className="p-5 text-right">
                        {item.level === 1 ? (
                          <span className="text-xs font-bold text-green-600 bg-green-50 px-2.5 py-1 rounded-lg border border-green-100">
                            Starter Level (0 Coins)
                          </span>
                        ) : (
                          <span className="text-xs font-semibold text-slate-500">
                            Requires {numVal.toLocaleString()} Coins
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Floating Save Bar */}
      <div className="sticky bottom-6 bg-slate-900/90 backdrop-blur-md text-white p-4 rounded-2xl shadow-2xl border border-slate-800 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <CheckCircle className="text-green-400" size={24} />
          <div>
            <div className="font-bold text-sm">Ready to save Level Configurations?</div>
            <div className="text-xs text-slate-400">Updates will reflect live across the Mobile App and User Profile API.</div>
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 bg-pink-600 hover:bg-pink-700 text-white px-6 py-2.5 rounded-xl font-extrabold shadow-lg shadow-pink-500/40 transition"
        >
          <Save size={18} /> Save {activeTab.toUpperCase()} Configs
        </button>
      </div>

      {/* Toast Notification */}
      {toast.visible && (
        <div className={`fixed bottom-6 right-6 z-[9999] px-6 py-3.5 rounded-2xl shadow-2xl border text-sm font-bold flex items-center gap-3 animate-in slide-in-from-bottom-5 fade-in duration-300 ${
          toast.type === 'error' ? 'bg-red-50 text-red-600 border-red-200' : 'bg-slate-900 text-green-400 border-slate-800'
        }`}>
          <span>{toast.message}</span>
        </div>
      )}
    </div>
  );
};

export default LevelSettings;
